import { useState, useEffect, useCallback } from 'react';

type DeviceSize = 'iPhoneSE' | 'iPhonePro' | 'mobile' | 'tablet' | 'desktop';

/**
 * Custom hook to determine if the viewport is mobile-sized
 * Uses MediaQueryList for efficient updates without resize event listeners
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      return window.innerWidth < 768;
    };
    
    // Initial check
    setIsMobile(checkMobile());
    
    // Setup media query
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };
    
    // Add listener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onChange);
    } else {
      // Older browsers fallback
      mediaQuery.addListener(onChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', onChange);
      } else {
        // Older browsers fallback
        mediaQuery.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
}

/**
 * Custom hook to check if the device matches a specific media query
 * @param query Media query string to check
 * @returns Whether the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // Check if window is available (SSR safety)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia(query);
    
    // Initial check
    setMatches(mediaQuery.matches);
    
    // Update matches when query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Older browsers
      mediaQuery.addListener(handleChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Custom hook to determine the current device size category
 * Uses ResizeObserver for efficient updates without resize event listeners
 */
export function useDeviceSize() {
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  
  useEffect(() => {
    // Function to calculate device size based on width
    const calculateDeviceSize = () => {
      const width = window.innerWidth;
      
      if (width < 375) {
        return 'iPhoneSE';
      } else if (width < 428) {
        return 'iPhonePro';
      } else if (width < 768) {
        return 'mobile';
      } else if (width < 1024) {
        return 'tablet';
      } else {
        return 'desktop';
      }
    };
    
    // Set initial size
    setDeviceSize(calculateDeviceSize());
    
    // Function to handle resize
    const handleResize = () => {
      const newSize = calculateDeviceSize();
      setDeviceSize(newSize);
    };
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return deviceSize;
}